<script setup>
import ConfirmationModal from '@/Components/ConfirmationModal.vue';
import CustomerSelect from '@/Components/CustomerSelect.vue';
import DialogModal from '@/Components/DialogModal.vue';
import TextField from '@/Components/TextField.vue';
import { Head, router, usePage } from '@inertiajs/vue3';
import { mdiCheckBold, mdiDelete, mdiPencil } from '@mdi/js';
import { computed, ref, watch } from 'vue';
import OutlinedSheet from '@/Components/OutlinedSheet.vue';

const page = usePage();
const accounts = ref([...page.props.accounts]);
const customerFilter = ref(page.props.filters.customer_number);
const showEditModal = ref(false);
const editProcessing = ref(false);
const deleteProcessingId = ref(null);
const swapDeleteProcessingId = ref(null);
const editingAccountId = ref(null);
const editName = ref('');
const editErrors = ref({});
const confirmationModal = ref({
    show: false,
    type: null,
    id: null,
    label: '',
});

watch(
    () => page.props.accounts,
    (value) => {
        accounts.value = [...value];
    },
);

watch(
    () => page.props.filters.customer_number,
    (value) => {
        customerFilter.value = value;
    },
);

const totalBalance = computed(() => {
    return accounts.value.reduce(
        (carry, account) => carry + account.balance,
        0,
    );
});

const recentSwaps = computed(() => {
    return page.props.recent_swaps;
});

const applyCustomerFilter = (value = customerFilter.value) => {
    customerFilter.value = value;
    router.get(
        route('pallet-swap.admin'),
        {
            customer_number: value,
        },
        {
            only: ['accounts', 'filters', 'recent_swaps', 'selected_customer'],
            preserveScroll: true,
            preserveState: true,
            replace: true,
        },
    );
};

const openEditModal = (account) => {
    editingAccountId.value = account.id;
    editName.value = account.name;
    editErrors.value = {};
    showEditModal.value = true;
};

const closeEditModal = () => {
    showEditModal.value = false;
};

const saveEdit = async () => {
    editProcessing.value = true;
    editErrors.value = {};

    try {
        const response = await axios.put(
            route('pallet-swap.account.update', {
                pallet_account: editingAccountId.value,
            }),
            {
                name: editName.value,
            },
        );

        accounts.value = accounts.value
            .map((account) =>
                account.id === response.data.id ? response.data : account,
            )
            .sort((left, right) => left.name.localeCompare(right.name));
        router.reload({ only: ['accounts', 'recent_swaps'] });
        closeEditModal();
    } catch (error) {
        if (error.response?.status === 422) {
            editErrors.value = error.response.data.errors;
        } else {
            throw error;
        }
    } finally {
        editProcessing.value = false;
    }
};

const deleteAccount = async (accountId) => {
    deleteProcessingId.value = accountId;

    try {
        await axios.delete(
            route('pallet-swap.account.destroy', {
                pallet_account: accountId,
            }),
        );

        accounts.value = accounts.value.filter(
            (account) => account.id !== accountId,
        );
        router.reload({ only: ['accounts', 'recent_swaps'] });
    } finally {
        deleteProcessingId.value = null;
    }
};

const deleteSwap = async (swapId) => {
    swapDeleteProcessingId.value = swapId;

    try {
        await axios.delete(
            route('pallet-swap.swap.destroy', {
                pallet_swap: swapId,
            }),
        );

        await router.reload({ only: ['accounts', 'recent_swaps'] });
    } finally {
        swapDeleteProcessingId.value = null;
    }
};

const openDeleteAccountConfirmation = (account) => {
    confirmationModal.value = {
        show: true,
        type: 'account',
        id: account.id,
        label: account.name,
    };
};

const openDeleteSwapConfirmation = (swap) => {
    confirmationModal.value = {
        show: true,
        type: 'swap',
        id: swap.id,
        label: `${swap.account_name} - ${swap.swap_date}`,
    };
};

const closeConfirmationModal = () => {
    confirmationModal.value = {
        show: false,
        type: null,
        id: null,
        label: '',
    };
};

const confirmDelete = async () => {
    if (confirmationModal.value.type === 'account') {
        await deleteAccount(confirmationModal.value.id);
    }

    if (confirmationModal.value.type === 'swap') {
        await deleteSwap(confirmationModal.value.id);
    }

    closeConfirmationModal();
};
</script>

<template>
    <v-container>
        <Head :title="$t('Pallet Swap Admin')" />

        <v-row class="tw-mb-4">
            <v-col cols="12" lg="12">
                <outlined-sheet
                    ><p class="tw-mb-2">
                        {{
                            $t('Filter pallet accounts and swaps by customer.')
                        }}
                    </p>
                    <CustomerSelect
                        v-model="customerFilter"
                        :placeholder="$t('Customer')"
                        :selected-customer="page.props.selected_customer"
                        clearable
                        @update:model-value="applyCustomerFilter"
                    />
                </outlined-sheet>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="6">
                <v-card>
                    <v-card-title>{{ $t('Accounts') }}</v-card-title>
                    <v-table density="comfortable">
                        <thead>
                            <tr>
                                <th>{{ $t('Account') }}</th>
                                <th class="tw-text-right">
                                    {{ $t('Balance') }}
                                </th>
                                <th class="tw-text-right">{{ $t('Swaps') }}</th>
                                <th class="tw-text-right">
                                    {{ $t('Actions') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="account in accounts" :key="account.id">
                                <td>{{ account.name }}</td>
                                <td class="tw-text-right">
                                    {{ account.balance }}
                                </td>
                                <td class="tw-text-right">
                                    {{ account.swaps_count }}
                                </td>
                                <td class="tw-whitespace-nowrap tw-text-right">
                                    <v-btn
                                        size="x-small"
                                        variant="text"
                                        color="primary"
                                        :icon="mdiPencil"
                                        @click="openEditModal(account)"
                                    />
                                    <v-btn
                                        size="x-small"
                                        variant="text"
                                        color="danger"
                                        :icon="mdiDelete"
                                        :loading="
                                            deleteProcessingId === account.id
                                        "
                                        @click="
                                            openDeleteAccountConfirmation(
                                                account,
                                            )
                                        "
                                    />
                                </td>
                            </tr>
                            <tr v-if="accounts.length === 0">
                                <td
                                    colspan="4"
                                    class="tw-text-center tw-text-slate-500"
                                >
                                    {{ $t('No pallet accounts yet.') }}
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </v-card>
            </v-col>
            <v-col cols="12" sm="6" lg="3">
                <v-card>
                    <v-card-text>
                        <div class="tw-text-sm tw-text-slate-500">
                            {{ $t('Accounts') }}
                        </div>
                        <div class="tw-text-2xl tw-font-semibold">
                            {{ accounts.length }}
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" sm="6" lg="3">
                <v-card>
                    <v-card-text>
                        <div class="tw-text-sm tw-text-slate-500">
                            {{ $t('Total Balance') }}
                        </div>
                        <div class="tw-text-2xl tw-font-semibold">
                            {{ totalBalance }}
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12">
                <v-card>
                    <v-card-title>{{ $t('Recent Swaps') }}</v-card-title>
                    <v-table density="comfortable">
                        <thead>
                            <tr>
                                <th>{{ $t('Date') }}</th>
                                <th>{{ $t('Account') }}</th>
                                <th>{{ $t('Customer') }}</th>
                                <th>{{ $t('Driver') }}</th>
                                <th>{{ $t('Number Plate') }}</th>
                                <th class="tw-text-right">
                                    {{ $t('Delivered') }}
                                </th>
                                <th class="tw-text-right">
                                    {{ $t('Received') }}
                                </th>
                                <th>{{ $t('Note') }}</th>
                                <th class="tw-text-center">
                                    {{ $t('Signed') }}
                                </th>
                                <th class="tw-text-right">
                                    {{ $t('Actions') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="swap in recentSwaps" :key="swap.id">
                                <td>{{ swap.swap_date }}</td>
                                <td>{{ swap.account_name }}</td>
                                <td>{{ swap.customer_name || '-' }}</td>
                                <td>{{ swap.driver_name }}</td>
                                <td>{{ swap.number_plate || '-' }}</td>
                                <td class="tw-text-right">
                                    {{ swap.delivered_pallets }}
                                </td>
                                <td class="tw-text-right">
                                    {{ swap.received_pallets }}
                                </td>
                                <td>{{ swap.note || '-' }}</td>
                                <td class="tw-text-center">
                                    <v-icon
                                        v-if="swap.signed"
                                        :icon="mdiCheckBold"
                                        color="success"
                                    />
                                    <span v-else>-</span>
                                </td>
                                <td class="tw-whitespace-nowrap tw-text-right">
                                    <v-btn
                                        size="x-small"
                                        variant="text"
                                        color="danger"
                                        :icon="mdiDelete"
                                        :loading="
                                            swapDeleteProcessingId === swap.id
                                        "
                                        @click="
                                            openDeleteSwapConfirmation(swap)
                                        "
                                    />
                                </td>
                            </tr>
                            <tr v-if="recentSwaps.length === 0">
                                <td
                                    colspan="10"
                                    class="tw-text-center tw-text-slate-500"
                                >
                                    {{ $t('No pallet swaps yet.') }}
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </v-card>
            </v-col>
        </v-row>

        <DialogModal :show="showEditModal" @close="closeEditModal">
            <template #title>{{ $t('Edit Pallet Account') }}</template>
            <template #content>
                <TextField
                    v-model.trim="editName"
                    :label="$t('Account Name')"
                    :error-messages="editErrors.name"
                />
            </template>
            <template #footer>
                <v-btn variant="text" @click="closeEditModal">
                    {{ $t('Cancel') }}
                </v-btn>
                <v-btn
                    color="primary"
                    variant="elevated"
                    :loading="editProcessing"
                    @click="saveEdit"
                >
                    {{ $t('Save') }}
                </v-btn>
            </template>
        </DialogModal>

        <ConfirmationModal
            :show="confirmationModal.show"
            @close="closeConfirmationModal"
        >
            <template #title>{{ $t('Confirm Delete') }}</template>
            <template #content>
                <p class="tw-text-sm tw-text-slate-700">
                    {{
                        confirmationModal.type === 'account'
                            ? $t(
                                  'Do you really want to delete the pallet account ":name"?',
                                  { name: confirmationModal.label },
                              )
                            : $t(
                                  'Do you really want to delete the pallet swap ":name"?',
                                  { name: confirmationModal.label },
                              )
                    }}
                </p>
            </template>
            <template #footer>
                <v-btn variant="text" @click="closeConfirmationModal">
                    {{ $t('Cancel') }}
                </v-btn>
                <v-btn
                    color="danger"
                    variant="elevated"
                    :loading="
                        confirmationModal.type === 'account'
                            ? deleteProcessingId === confirmationModal.id
                            : swapDeleteProcessingId === confirmationModal.id
                    "
                    @click="confirmDelete"
                >
                    {{ $t('Delete') }}
                </v-btn>
            </template>
        </ConfirmationModal>
    </v-container>
</template>
